import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  cashierService, type CollectFeePayload, type FeePayment, type LateFeeRule,
  type PaymentMode, type Quarter, type Stream, type StudentFeeSummary,
} from "@/lib/services";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { formatINR } from "@/lib/utils";
import { AlertTriangle, Banknote, Check, Download, FileSpreadsheet, FileText, Loader2, Printer, Receipt, Search, Settings, Wallet } from "lucide-react";
import { toast } from "sonner";

const todayISO = () => new Date().toISOString().slice(0, 10);

export function CashierPage() {
  const { user } = useAuth();
  const canCollect = user?.role === "admin" || user?.role === "cashier";
  const isAdmin = user?.role === "admin" || user?.role === "principal";
  const [gr, setGr] = useState("");
  const [searched, setSearched] = useState<string | null>(null);
  const [collectFor, setCollectFor] = useState<{ quarter: Quarter; tuition: number; lateFee: number } | null>(null);
  const [lastReceipt, setLastReceipt] = useState<FeePayment | null>(null);
  const [ruleOpen, setRuleOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);

  const summaryQ = useQuery({
    queryKey: ["cashier", "student", searched],
    queryFn: () => cashierService.studentSummary(searched!),
    enabled: !!searched,
    retry: false,
  });

  const todayQ = useQuery({
    queryKey: ["cashier", "today"],
    queryFn: () => cashierService.collections(todayISO()),
    enabled: canCollect,
    refetchInterval: 30_000,
  });

  if (!canCollect) {
    return (
      <div className="flex h-40 flex-col items-center justify-center gap-2 text-muted-foreground">
        <Wallet className="h-8 w-8" />
        <div className="text-lg font-semibold">Restricted</div>
        <div className="text-sm">Only administrators and cashiers can collect fees here.</div>
      </div>
    );
  }

  const summary = summaryQ.data;
  const student = summary?.student;
  const today = todayQ.data?.summary;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Banknote className="h-7 w-7 text-primary" /> Collect Fees
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Search a student by GR No., pick the quarter to collect, and generate an A5 dual-copy receipt.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={() => setRuleOpen(true)}>
              <Settings className="mr-2 h-4 w-4" /> Late-fee rule
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setReportsOpen(true)}>
            <FileText className="mr-2 h-4 w-4" /> Reports
          </Button>
        </div>
      </div>

      {/* Today's totals strip */}
      {today && (
        <Card>
          <CardContent className="grid grid-cols-2 gap-4 p-4 md:grid-cols-5">
            <TodayStat label="Today's receipts" value={String(today.count)} />
            <TodayStat label="Total" value={formatINR(today.total)} tone="primary" />
            <TodayStat label="Cash" value={formatINR(today.by_mode.cash)} />
            <TodayStat label="Online" value={formatINR(today.by_mode.online)} />
            <TodayStat label="Cheque" value={formatINR(today.by_mode.cheque)} />
          </CardContent>
        </Card>
      )}

      {/* Search bar */}
      <Card>
        <CardContent className="flex flex-col gap-3 p-5 md:flex-row md:items-end">
          <div className="flex-1">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">GR Number</Label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                autoFocus
                value={gr}
                placeholder="e.g. 1520"
                onChange={(e) => setGr(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") setSearched(gr.trim()); }}
                className="pl-9"
              />
            </div>
          </div>
          <Button onClick={() => setSearched(gr.trim())} disabled={!gr.trim() || summaryQ.isFetching}>
            {summaryQ.isFetching ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
            Find student
          </Button>
        </CardContent>
      </Card>

      {searched && summaryQ.isError && (
        <Card><CardContent className="p-6 text-center text-destructive">
          No student found with GR No. <span className="font-bold">{searched}</span>. Please verify and try again.
        </CardContent></Card>
      )}

      {student && summary && (
        <>
          <StudentHeader summary={summary} />
          <QuartersGrid
            summary={summary}
            onCollect={(q) => setCollectFor(q)}
            onOpenReceipt={(r) => setLastReceipt(r)}
          />
        </>
      )}

      {collectFor && student && summary && (
        <CollectFeeDialog
          student={student}
          stream={summary.stream}
          quarter={collectFor.quarter}
          tuitionDue={collectFor.tuition}
          lateFeeDue={collectFor.lateFee}
          onClose={() => setCollectFor(null)}
          onCollected={(r) => { setCollectFor(null); setLastReceipt(r); }}
        />
      )}

      {lastReceipt && (
        <ReceiptDialog receipt={lastReceipt} onClose={() => setLastReceipt(null)} />
      )}

      {ruleOpen && <LateFeeRuleDialog onClose={() => setRuleOpen(false)} />}
      {reportsOpen && <ReportsDialog onClose={() => setReportsOpen(false)} />}
    </div>
  );
}

function TodayStat({ label, value, tone }: { label: string; value: string; tone?: "primary" }) {
  const color = tone === "primary" ? "text-primary" : "text-foreground";
  return (
    <div>
      <div className={`text-lg font-bold tabular-nums ${color}`}>{value}</div>
      <div className="text-xs uppercase text-muted-foreground tracking-wide">{label}</div>
    </div>
  );
}

function StudentHeader({ summary }: { summary: StudentFeeSummary }) {
  const s = summary.student!;
  const initials = (s.name || "?").split(" ").filter(Boolean).slice(0, 2)
    .map((x) => x[0]?.toUpperCase() || "").join("");
  const total = summary.quarters.reduce((a, q) => a + q.due, 0);
  const paid = summary.quarters.reduce((a, q) => a + q.paid, 0);
  return (
    <Card>
      <CardContent className="flex flex-wrap items-center gap-5 p-5">
        {s.photo_base64 ? (
          <img src={s.photo_base64} alt={s.name} className="h-20 w-20 rounded-full border border-border object-cover" />
        ) : (
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10 text-2xl font-bold text-primary">
            {initials || "?"}
          </div>
        )}
        <div className="flex-1 min-w-[240px]">
          <div className="text-xl font-bold">{s.name}</div>
          <div className="mt-1 flex flex-wrap gap-2 text-sm text-muted-foreground">
            <Badge variant="secondary">GR {s.gr_no || "—"}</Badge>
            <Badge variant="outline">Class {s.class_name}{s.section ? `-${s.section}` : ""}</Badge>
            {summary.stream !== "general" && <Badge variant="outline" className="capitalize">{summary.stream}</Badge>}
            {s.father_name && <span>Father: {s.father_name}</span>}
            {s.phone && <span>· {s.phone}</span>}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-6 text-right">
          <Stat label="Annual due" value={formatINR(total)} />
          <Stat label="Paid" value={formatINR(paid)} tone="ok" />
          <Stat label="Balance" value={formatINR(Math.max(0, total - paid))} tone="warn" />
        </div>
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: "ok" | "warn" }) {
  const color = tone === "ok" ? "text-emerald-600" : tone === "warn" ? "text-amber-600" : "text-foreground";
  return (
    <div>
      <div className={`text-lg font-bold ${color}`}>{value}</div>
      <div className="text-xs uppercase text-muted-foreground tracking-wide">{label}</div>
    </div>
  );
}

function QuartersGrid({ summary, onCollect, onOpenReceipt }: {
  summary: StudentFeeSummary;
  onCollect: (q: { quarter: Quarter; tuition: number; lateFee: number }) => void;
  onOpenReceipt: (r: FeePayment) => void;
}) {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
      {summary.quarters.map((q) => {
        const isPaid = q.status === "paid";
        const hasLateFee = q.late_fee_pending > 0;
        return (
          <Card key={q.quarter} className={isPaid ? "border-emerald-300 bg-emerald-50/40 dark:bg-emerald-950/10" : hasLateFee ? "border-amber-300" : ""}>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center justify-between">
                <span>{q.label}</span>
                <Badge variant={isPaid ? "default" : q.status === "partial" ? "secondary" : "outline"}>
                  {isPaid ? <><Check className="mr-1 h-3 w-3" /> Paid</> : q.status === "partial" ? "Partial" : "Pending"}
                </Badge>
              </CardTitle>
              <CardDescription className="space-y-0.5">
                <div>{q.months.join(" · ")}</div>
                <div className="text-[11px]">Due by {new Date(q.due_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</div>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <Row label="Tuition due" value={formatINR(q.tuition_due)} />
              <Row label="Tuition paid" value={formatINR(q.tuition_paid)} tone="ok" />
              {hasLateFee && (
                <Row
                  label={
                    <span className="flex items-center gap-1"><AlertTriangle className="h-3 w-3 text-amber-500" /> Late fee ({q.days_past_due}d)</span>
                  }
                  value={formatINR(q.late_fee_pending)}
                  tone="warn"
                />
              )}
              <div className="border-t border-border pt-2">
                <Row label={<span className="font-semibold">Balance</span>} value={formatINR(q.pending)} tone={q.pending > 0 ? "warn" : "ok"} />
              </div>
              <div className="pt-2 flex flex-col gap-2">
                {q.payments.length > 0 && (
                  <div className="rounded-md border border-border p-2 text-xs">
                    <div className="mb-1 font-semibold text-muted-foreground">Receipts</div>
                    {q.payments.map((p) => (
                      <button key={p.id}
                        onClick={() => onOpenReceipt(p)}
                        className="block w-full text-left hover:text-primary">
                        <span className="font-mono">{p.receipt_no}</span>
                        <span className="ml-2 text-muted-foreground">{formatINR(p.amount)} · {p.payment_mode}</span>
                      </button>
                    ))}
                  </div>
                )}
                {q.pending > 0 && (
                  <Button size="sm" onClick={() => onCollect({ quarter: q.quarter, tuition: q.tuition_pending, lateFee: q.late_fee_pending })}>
                    <Receipt className="mr-2 h-4 w-4" /> Collect {formatINR(q.pending)}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function Row({ label, value, tone }: { label: React.ReactNode; value: string; tone?: "ok" | "warn" }) {
  const color = tone === "ok" ? "text-emerald-600" : tone === "warn" ? "text-amber-600" : "text-foreground";
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={`font-semibold tabular-nums ${color}`}>{value}</span>
    </div>
  );
}

function CollectFeeDialog({ student, stream, quarter, tuitionDue, lateFeeDue, onClose, onCollected }: {
  student: NonNullable<StudentFeeSummary["student"]>;
  stream: Stream;
  quarter: Quarter;
  tuitionDue: number;
  lateFeeDue: number;
  onClose: () => void;
  onCollected: (r: FeePayment) => void;
}) {
  const [mode, setMode] = useState<PaymentMode>("cash");
  const [amount, setAmount] = useState<string>(tuitionDue.toString());
  const [waiveLateFee, setWaiveLateFee] = useState(false);
  const [txn, setTxn] = useState("");
  const [chequeNo, setChequeNo] = useState("");
  const [chequeBank, setChequeBank] = useState("");
  const [chequeDate, setChequeDate] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const qc = useQueryClient();

  const effectiveLate = waiveLateFee ? 0 : lateFeeDue;
  const total = Number(amount || 0) + effectiveLate;

  const submit = useMutation({
    mutationFn: () => {
      const body: CollectFeePayload = {
        student_id: student.id,
        quarter,
        stream,
        amount: Number(amount),
        include_late_fee: !waiveLateFee,
        override_late_fee: waiveLateFee ? 0 : null,
        payment_mode: mode,
        note: note || undefined,
      };
      if (mode === "online") body.transaction_no = txn.trim();
      if (mode === "cheque") {
        body.cheque_no = chequeNo.trim();
        body.cheque_bank = chequeBank.trim();
        body.cheque_date = chequeDate;
      }
      return cashierService.collect(body);
    },
    onSuccess: (r) => {
      toast.success(`Receipt ${r.receipt_no} generated`);
      qc.invalidateQueries({ queryKey: ["cashier"] });
      onCollected(r);
    },
    onError: (e: any) => toast.error(e.message || "Collection failed"),
  });

  const canSubmit = useMemo(() => {
    if (total <= 0) return false;
    if (mode === "online" && !txn.trim()) return false;
    if (mode === "cheque" && !chequeNo.trim()) return false;
    return true;
  }, [total, mode, txn, chequeNo]);

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Collect fee — Quarter {quarter}</DialogTitle>
          <DialogDescription>
            {student.name} · GR {student.gr_no || "—"} · Class {student.class_name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Tuition amount (₹)</Label>
            <Input type="number" min={0} value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>

          {lateFeeDue > 0 && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:bg-amber-950/30 dark:border-amber-800">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-amber-800 dark:text-amber-300">
                  <AlertTriangle className="h-4 w-4" />
                  <span className="font-semibold">Late Fee</span>
                </div>
                <span className="tabular-nums font-semibold">{formatINR(lateFeeDue)}</span>
              </div>
              <label className="mt-2 flex items-center gap-2 text-xs">
                <input type="checkbox" checked={waiveLateFee} onChange={(e) => setWaiveLateFee(e.target.checked)} />
                Waive late fee for this transaction
              </label>
            </div>
          )}

          <div className="flex items-center justify-between rounded-md bg-primary/5 p-3">
            <span className="text-sm text-muted-foreground">Total to collect</span>
            <span className="text-lg font-bold tabular-nums text-primary">{formatINR(total)}</span>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Payment mode</Label>
            <div className="mt-1 grid grid-cols-3 gap-2">
              {(["cash", "online", "cheque"] as PaymentMode[]).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMode(m)}
                  className={`rounded-md border px-3 py-2 text-sm font-medium transition
                    ${mode === m ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background hover:bg-muted"}`}
                >
                  {m === "cash" ? "Cash" : m === "online" ? "Online / UPI" : "Cheque"}
                </button>
              ))}
            </div>
          </div>

          {mode === "online" && (
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Transaction number</Label>
              <Input value={txn} onChange={(e) => setTxn(e.target.value)} placeholder="e.g. UPI-9988XXXX" />
            </div>
          )}
          {mode === "cheque" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Cheque No.</Label>
                <Input value={chequeNo} onChange={(e) => setChequeNo(e.target.value)} />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Bank</Label>
                <Input value={chequeBank} onChange={(e) => setChequeBank(e.target.value)} placeholder="HDFC / SBI…" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Cheque date</Label>
                <Input type="date" value={chequeDate} onChange={(e) => setChequeDate(e.target.value)} />
              </div>
            </div>
          )}

          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Note (optional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => submit.mutate()} disabled={!canSubmit || submit.isPending}>
            {submit.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Receipt className="mr-2 h-4 w-4" />}
            Collect & generate receipt
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReceiptDialog({ receipt, onClose }: { receipt: FeePayment; onClose: () => void }) {
  const [url, setUrl] = useState<string | null>(null);

  const load = useMutation({
    mutationFn: () => cashierService.openReceiptPdf(receipt.receipt_no),
    onSuccess: (u) => setUrl(u),
    onError: (e: any) => toast.error(e.message || "Could not load PDF"),
  });

  useMemo(() => { load.mutate(); return null; }, [receipt.receipt_no]); // trigger once

  const download = () => {
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = `receipt-${receipt.receipt_no}.pdf`;
    a.click();
  };
  const printPdf = () => {
    if (!url) return;
    const w = window.open(url, "_blank", "noopener");
    if (w) setTimeout(() => { try { w.focus(); w.print(); } catch (_e) { /* browser will handle */ } }, 500);
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) { if (url) URL.revokeObjectURL(url); onClose(); } }}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Receipt className="h-5 w-5" /> Receipt {receipt.receipt_no}</DialogTitle>
          <DialogDescription>
            {receipt.student_name} · Class {receipt.class_name}{receipt.section ? `-${receipt.section}` : ""} · {receipt.months.join(", ")} · {formatINR(receipt.amount)} · {receipt.payment_mode}
          </DialogDescription>
        </DialogHeader>

        <div className="h-[520px] w-full overflow-hidden rounded-md border border-border">
          {url ? (
            <iframe src={url} className="h-full w-full" title="Receipt" />
          ) : (
            <div className="flex h-full items-center justify-center text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Rendering receipt…
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button variant="outline" onClick={download} disabled={!url}>Download PDF</Button>
          <Button onClick={printPdf} disabled={!url}>
            <Printer className="mr-2 h-4 w-4" /> Print
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function LateFeeRuleDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const { data, isPending } = useQuery({
    queryKey: ["cashier", "late-fee-rule"],
    queryFn: () => cashierService.getLateFeeRule(),
  });
  const [rule, setRule] = useState<LateFeeRule | null>(null);
  useMemo(() => { if (data && !rule) setRule(data); return null; }, [data, rule]);

  const save = useMutation({
    mutationFn: (r: LateFeeRule) => cashierService.setLateFeeRule(r),
    onSuccess: () => {
      toast.success("Late-fee rule updated");
      qc.invalidateQueries({ queryKey: ["cashier"] });
      onClose();
    },
    onError: (e: any) => toast.error(e.message || "Save failed"),
  });

  const r = rule || data;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Late Fee Rule</DialogTitle>
          <DialogDescription>
            Applies to all quarters past their due date across the school. Waivers can be set per transaction at collection time.
          </DialogDescription>
        </DialogHeader>
        {isPending || !r ? (
          <div className="flex h-24 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        ) : (
          <div className="space-y-4">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={r.enabled} onChange={(e) => setRule({ ...r, enabled: e.target.checked })} />
              Late fee engine enabled
            </label>

            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Mode</Label>
              <div className="mt-1 grid grid-cols-3 gap-2">
                {(["per_day", "flat", "off"] as const).map((m) => (
                  <button key={m} type="button"
                    onClick={() => setRule({ ...r, mode: m })}
                    className={`rounded-md border px-3 py-2 text-sm font-medium transition
                      ${r.mode === m ? "border-primary bg-primary text-primary-foreground" : "border-border bg-background hover:bg-muted"}`}>
                    {m === "per_day" ? "Per day" : m === "flat" ? "Flat" : "Off"}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Grace days</Label>
                <Input type="number" min={0} value={r.grace_days}
                  onChange={(e) => setRule({ ...r, grace_days: Number(e.target.value) })} />
              </div>
              {r.mode === "per_day" ? (
                <>
                  <div>
                    <Label className="text-xs uppercase tracking-wide text-muted-foreground">Amount / day (₹)</Label>
                    <Input type="number" min={0} value={r.per_day_amount}
                      onChange={(e) => setRule({ ...r, per_day_amount: Number(e.target.value) })} />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs uppercase tracking-wide text-muted-foreground">Max cap (₹, 0 = no cap)</Label>
                    <Input type="number" min={0} value={r.max_amount}
                      onChange={(e) => setRule({ ...r, max_amount: Number(e.target.value) })} />
                  </div>
                </>
              ) : r.mode === "flat" ? (
                <div>
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">Flat amount (₹)</Label>
                  <Input type="number" min={0} value={r.flat_amount}
                    onChange={(e) => setRule({ ...r, flat_amount: Number(e.target.value) })} />
                </div>
              ) : null}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => rule && save.mutate(rule)} disabled={!rule || save.isPending}>
            {save.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Save rule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReportsDialog({ onClose }: { onClose: () => void }) {
  const [date, setDate] = useState<string>(todayISO());
  const q = useQuery({
    queryKey: ["cashier", "report", date],
    queryFn: () => cashierService.collections(date),
    enabled: !!date,
  });

  const download = useMutation({
    mutationFn: (fmt: "pdf" | "xlsx") => cashierService.cashbookDownload(date, fmt),
    onError: (e: any) => toast.error(e.message || "Download failed"),
  });

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" /> Fee-collection reports
          </DialogTitle>
          <DialogDescription>Daily cash-book · Filter by date and export to PDF or Excel.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div className="ml-auto flex gap-2">
              <Button variant="outline" onClick={() => download.mutate("pdf")} disabled={download.isPending}>
                <Download className="mr-2 h-4 w-4" /> PDF
              </Button>
              <Button variant="outline" onClick={() => download.mutate("xlsx")} disabled={download.isPending}>
                <FileSpreadsheet className="mr-2 h-4 w-4" /> Excel
              </Button>
            </div>
          </div>

          {q.isPending ? (
            <div className="flex h-32 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
          ) : q.data ? (
            <>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
                <TodayStat label="Receipts" value={String(q.data.summary.count)} />
                <TodayStat label="Total" value={formatINR(q.data.summary.total)} tone="primary" />
                <TodayStat label="Cash" value={formatINR(q.data.summary.by_mode.cash)} />
                <TodayStat label="Online" value={formatINR(q.data.summary.by_mode.online)} />
                <TodayStat label="Cheque" value={formatINR(q.data.summary.by_mode.cheque)} />
              </div>
              <div className="max-h-[300px] overflow-auto rounded-md border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/60 sticky top-0">
                    <tr className="text-left">
                      <th className="p-2">Receipt</th>
                      <th className="p-2">Student</th>
                      <th className="p-2">Class</th>
                      <th className="p-2">Q</th>
                      <th className="p-2">Mode</th>
                      <th className="p-2 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {q.data.rows.map((row) => (
                      <tr key={row.receipt_no} className="border-t border-border">
                        <td className="p-2 font-mono">{row.receipt_no}</td>
                        <td className="p-2">
                          <div>{row.student_name}</div>
                          <div className="text-xs text-muted-foreground">GR {row.gr_no || "—"}</div>
                        </td>
                        <td className="p-2">{row.class_name}{row.section ? `-${row.section}` : ""}</td>
                        <td className="p-2">Q{row.quarter}</td>
                        <td className="p-2 capitalize">{row.payment_mode}</td>
                        <td className="p-2 text-right font-semibold tabular-nums">{formatINR(row.amount)}</td>
                      </tr>
                    ))}
                    {q.data.rows.length === 0 && (
                      <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No collections on this date.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
